import {
  deriveCreditsState,
  deriveUsageMetrics,
  useAgentUsageMeter,
  useBuilderBalance,
  type CreditsState,
  type CreditsUsage,
} from '@/features/billing-usage';
import { useBillingUsageLoad } from '@/services/billing/use-billing-usage';
import { useBillingPeriod } from './use-billing-period';

export type CreditsSummary =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | {
      status: 'ready';
      state: CreditsState;
      balanceCents: number;
      topUpBalanceCents: number;
      lastTopUpCents: number;
      hasFunded: boolean;
      usage: CreditsUsage;
    };

/** Spend per day in the current billing period. 0 when the day in the period is not known. */
function dailySpendCents(periodSpendCents: number, dayNumber: number | undefined) {
  if (dayNumber == null || dayNumber < 1) return 0;
  return periodSpendCents / dayNumber;
}

export function useCreditsSummary(): CreditsSummary {
  const balance = useBuilderBalance();
  const usageLoad = useBillingUsageLoad({ silent: true });
  const period = useBillingPeriod();
  const { meter } = useAgentUsageMeter();

  if (balance.isError) return { status: 'unavailable' };
  // Wait for usage too. The tone uses the spend rate, and must not change after the pill shows.
  if (balance.isLoading || !usageLoad.hasSettled) return { status: 'loading' };
  // The load also settles when it fails. Do not show zeros as real usage.
  if (usageLoad.usage == null) return { status: 'unavailable' };

  const metrics = deriveUsageMetrics(usageLoad.usage);
  const emailSpendCents = Math.round(metrics.billableMessagesSent * metrics.builderCreditCostPerMessage * 100);
  const usage = {
    emailSpendCents,
    emailSends: metrics.billableMessagesSent,
    activeProfiles: metrics.monthlyActiveUsers,
    storedProfiles: metrics.totalCustomerCount,
    agent:
      meter == null
        ? null
        : { used: meter.used_credits, allowance: meter.allowance_credits, enforcing: meter.enforcing },
  } satisfies CreditsUsage;

  const state = deriveCreditsState({
    balanceCents: balance.balanceCents,
    hasFunded: balance.hasFunded,
    dailySpendCents: dailySpendCents(emailSpendCents, period.dayNumber),
    activeProfiles: usage.activeProfiles,
    storedProfiles: usage.storedProfiles,
  });

  return {
    status: 'ready',
    state,
    balanceCents: balance.balanceCents,
    topUpBalanceCents: balance.topUpBalanceCents,
    lastTopUpCents: balance.lastTopUpCents,
    hasFunded: balance.hasFunded,
    usage,
  };
}
